import { hashTileRange, locateNode, TILE_WIDTH, type NodeSource, type TileCoord } from '../merkle/tile';

export type TileFetcher = (tile: TileCoord) => Promise<Uint8Array>;

/**
 * Serves perfect-subtree hashes out of hash tiles, so the generic RFC 6962 PATH/SUBPROOF builders
 * can produce proofs from a static-ct-api log. Tiles are fetched at most once per instance.
 */
export class TileNodeSource implements NodeSource {
  private tiles = new Map<string, Promise<Uint8Array>>();

  constructor(
    private readonly fetchTile: TileFetcher,
    readonly treeSize: number,
  ) {}

  private tile(coord: TileCoord): Promise<Uint8Array> {
    const key = `${coord.level}/${coord.index}`;
    let p = this.tiles.get(key);
    if (!p) {
      p = this.fetchTile(coord);
      this.tiles.set(key, p);
    }
    return p;
  }

  async node(level: number, index: number): Promise<Uint8Array> {
    const { tile, offset, span } = locateNode(level, index, this.treeSize);
    if (tile.width === 0 || offset + span > tile.width) {
      throw new RangeError(`node (${level}, ${index}) is not a complete subtree of a tree with ${this.treeSize} leaves`);
    }
    const bytes = await this.tile(tile);
    if (bytes.length < (offset + span) * 32) {
      throw new RangeError(`tile ${tile.level}/${tile.index} is shorter than expected (${bytes.length} bytes, need ${(offset + span) * 32})`);
    }
    return hashTileRange(bytes, offset, span);
  }

  /** Number of distinct tiles fetched so far (diagnostics). */
  get tilesFetched(): number {
    return this.tiles.size;
  }
}

export { TILE_WIDTH };
